import React from "react";
import InvoiceField from "../component/InvoiceFiled";

const SalesForm = ({ data = {}, onUpdate = () => {} }) => {
  const items = data?.items || [];

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };

    if (field === "qty" || field === "rate") {
      const qty = Number(newItems[index].qty) || 0;
      const rate = Number(newItems[index].rate) || 0;
      newItems[index].amount = (qty * rate).toFixed(2);
    }
    onUpdate("items", newItems);
  };

  const addItem = () => {
    const newItems = [...items, { id: items.length + 1, desc: "", qty: 0, rate: 0, amount: 0 }];
    onUpdate("items", newItems);
  };

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onUpdate("items", newItems);
  };

  // Calculate totals
  const subtotal = items.reduce((acc, item) => acc + Number(item.amount || 0), 0);
  const taxRate = Number(data?.taxRate || 0);
  const tax = (subtotal * taxRate) / 100;
  const shipping = Number(data?.sh || 0);
  const other = Number(data?.other || 0);
  const total = (subtotal + tax + shipping + other).toFixed(2);

  // Save to localStorage
  const handleSave = () => {
    const savedData = {
      ...data,
      items,
      subtotal: subtotal.toFixed(2),
      taxRate,
      tax: tax.toFixed(2),
      total,
      sh: shipping,
      other
    };
    localStorage.setItem("salesInvoiceData", JSON.stringify(savedData));
    alert("Invoice saved locally!");
  };

  return (
    <div className="space-y-6 p-4 border rounded bg-white shadow-sm">
      {/* Header */}
      <h3 className="font-bold text-lg text-[#4F6D95] border-b pb-2 uppercase tracking-wide">Sales Invoice Form</h3>

      {/* Company & Invoice Info */}
      <div className="grid grid-cols-2 gap-4">
        <InvoiceField label="Company Name" value={data?.company?.name || ""} onChange={(v) => onUpdate("company", { ...data.company, name: v })} />
        <InvoiceField label="Invoice #" value={data?.invoiceNumber || ""} onChange={(v) => onUpdate("invoiceNumber", v)} />
      </div>

      {/* Customer Info */}
      <div className="grid grid-cols-2 gap-4">
        <InvoiceField label="Customer Name" value={data?.customer?.name || ""} onChange={(v) => onUpdate("customer", { ...data.customer, name: v })} />
        <InvoiceField label="Customer ID" value={data?.customer?.id || ""} onChange={(v) => onUpdate("customer", { ...data.customer, id: v })} />
      </div>
      <InvoiceField label="Customer Address" value={data?.customer?.address || ""} onChange={(v) => onUpdate("customer", { ...data.customer, address: v })} />

      {/* Shipping & Payment */}
      <div className="grid grid-cols-2 gap-4">
        <InvoiceField label="Salesperson" value={data?.salesperson || ""} onChange={(v) => onUpdate("salesperson", v)} />
        <InvoiceField label="P.O. Number" value={data?.poNumber || ""} onChange={(v) => onUpdate("poNumber", v)} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <InvoiceField label="Ship Date" type="date" value={data?.shipDate || ""} onChange={(v) => onUpdate("shipDate", v)} />
        <InvoiceField label="Ship Via" value={data?.shipVia || ""} onChange={(v) => onUpdate("shipVia", v)} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <InvoiceField label="F.O.B." value={data?.fob || ""} onChange={(v) => onUpdate("fob", v)} />
        <InvoiceField label="Payment Terms" type="dropdown" value={data?.terms || ""} options={["Net 30", "Net 60", "Due on Receipt"]} onChange={(v) => onUpdate("terms", v)} />
      </div>

      {/* Charges */}
      <div className="grid grid-cols-2 gap-4 pt-4 border-t">
        <InvoiceField label="Shipping & Handling (₹)" type="number" value={data?.sh || 0} onChange={(v) => onUpdate("sh", v)} />
        <InvoiceField label="Other Charges (₹)" type="number" value={data?.other || 0} onChange={(v) => onUpdate("other", v)} />
      </div>
      <InvoiceField label="GST (%)" type="number" value={data?.taxRate || 0} onChange={(v) => onUpdate("taxRate", v)} />

      {/* Items Table */}
      <div className="pt-4 border-t">
        <h4 className="text-xs font-bold text-gray-400 mb-2 uppercase">Items</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-[#4F6D95] text-white uppercase">
              <tr>
                <th className="border px-2 py-1">#</th>
                <th className="border px-2 py-1">Description</th>
                <th className="border px-2 py-1">Qty</th>
                <th className="border px-2 py-1">Rate (₹)</th>
                <th className="border px-2 py-1">Amount (₹)</th>
                <th className="border px-2 py-1">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index}>
                  <td className="border px-2 py-1 text-center">{item.id}</td>
                  <td className="border px-2 py-1">
                    <InvoiceField value={item.desc} onChange={(v) => updateItem(index, "desc", v)} />
                  </td>
                  <td className="border px-2 py-1 text-center">
                    <InvoiceField type="number" value={item.qty} onChange={(v) => updateItem(index, "qty", v)} />
                  </td>
                  <td className="border px-2 py-1 text-right">
                    <InvoiceField type="number" value={item.rate} onChange={(v) => updateItem(index, "rate", v)} />
                  </td>
                  <td className="border px-2 py-1 text-right">{item.amount}</td>
                  <td className="border px-2 py-1 text-center">
                    <button onClick={() => removeItem(index)} className="text-red-500 text-xs">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addItem} className="mt-2 px-3 py-1 bg-[#4F6D95] text-white text-xs rounded">Add Item</button>
        </div>
      </div>

      {/* Totals */}
      <div className="flex justify-end gap-4 mt-4 text-xs">
        <div className="space-y-1">
          <div className="flex justify-between"><span>Subtotal:</span><span>₹{subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>GST ({taxRate}%):</span><span>₹{tax.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Shipping & Handling:</span><span>₹{shipping.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Other Charges:</span><span>₹{other.toFixed(2)}</span></div>
          <div className="flex justify-between font-bold text-[#4F6D95] border-t pt-1">Total: <span>₹{total}</span></div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end mt-6">
        <button onClick={handleSave} className="px-4 py-2 bg-[#4F6D95] text-white rounded hover:bg-blue-700">
          Save Invoice
        </button>
      </div>
    </div>
  );
};

export default SalesForm;
